/**
 * order-access — the buyer's ticket: the artifact they keep, return to, and
 * present at the door, not a receipt. The SAME surface renders whether it's
 * reached seconds after paying (same session, curious and a little
 * impatient) or days later from the fulfillment email, cold, on a strange
 * phone, with no session — so every branch below stands alone with nothing
 * left over from a shopping flow.
 *
 * Pure view — zero I/O, renders order-access.state.ts's OrderAccessData and
 * mutates nothing; the mounted app's own order-access.sync.ts resolves the
 * real (or, pre-fulfillment, the client-known) backing into that
 * projection. `status` mirrors OrderStatusSchema plus loading/not-found; the
 * "paid" branch additionally carries `fulfillment` (issuing → partial →
 * complete), since a real order can sit at orderStatus:"paid" — captured,
 * genuinely safe — for a real span before ticket/artifact minting catches
 * up, and that gap is rendered honestly rather than as an anonymous spinner.
 *
 * ACHROMATIC BUT FOR ONE EVENT, EDITORIAL — the same register as the real
 * ticket this page exists to deliver (@momo/ticket-template's
 * editorial/strata cards): achromatic ground, typographic hierarchy instead
 * of color or chrome, the ticket itself as the one unmistakable hero. Every
 * control on this surface is achromatic deliberately — buttons and
 * icon-buttons carry `color="neutral"`; there is no green "payment
 * confirmed" chrome, no colored status badge, no gradient identity banner.
 * The single exception is champagne gold on a ticket the holder has given
 * away (`ticketCard` below) — order-card.view.ts's own guest grammar,
 * reused verbatim — and it earns the page's one chromatic event by marking
 * the tickets that are no longer this page's own. Each ticket
 * card mounts the REAL production card live (`<ticket-card>`,
 * ticket-templates/ticket-card.view.ts — the one element every surface that
 * shows a ticket mounts, so the display id and brand a buyer reads here are
 * the same ones an operator reads on the order sheet) — the exact
 * Editorial/Strata composition the buyer's ticket resolved to at fulfillment,
 * never a hand-rolled stand-in, and laid out at its native 384px design width
 * and scaled whole to the screen, so a phone gets a smaller ticket rather
 * than a differently arranged one. This page tells that card only what it
 * alone holds — the token-scoped event/venue snapshot (`ticketCard` below);
 * the card derives the rest. Saving a card as a file rasterizes an
 * untransformed 384px twin of it on-device (`captureTicketCardImage`,
 * ticket-templates/capture.ts) — one card rendering, one capture path,
 * nothing that can drift. That twin is mounted ON DEMAND, one card at a time,
 * and taken back down after: a card carrying its own permanent twin is a
 * second full render of the same ticket, QR encoder included, so a rail of N
 * cards was drawing 2N codes for a file most buyers never ask for. The run
 * therefore drives the twin it mounts rather than looking for one in the
 * rail, which is also what lets it save a card the rail is not showing.
 *
 * Multi-ticket presentation is a horizontal scrolling carousel, not a
 * vertical stack: stacking scales the PAGE's length with ticket count (the
 * 5-ticket case burying the artifact rail and venue info far below the
 * fold), while a carousel confines that cost to one row's height regardless
 * of N and keeps every ticket at full, legible size. A trailing
 * skeleton-shaped card (the same 2:3 silhouette as a real card) stands in
 * for "more are being issued" during "partial" fulfillment — never a
 * fabricated ticket, since a minted qrToken would draw a scannable-looking
 * QR for a ticket that doesn't exist yet.
 *
 * Each ticket card carries one named text control in a band straddling its
 * own bottom-left corner. It is the question while nobody holds the ticket
 * ("Für wen?") and the answer once somebody does ("Für Lena · persönlich"),
 * and either way it opens the single sheet (handover-sheet.view.ts) where the
 * holder names the person, adds any further tickets going to them, and picks
 * how it travels. What travels is a
 * link to this same page carrying exactly that slice: the recipient gets
 * real cards, both wallets, and this same control, which is what lets them
 * hand one on again. That sameness is the whole model — this page runs no
 * branch asking whether it was reached by an order's token or a slice's, and
 * an `if` that asked would be the defect. .ics and receipt sit in the
 * header's own actions slot and stay there: the receipt carries buyer PII and
 * is one-per-order regardless of ticket count, add-to-calendar is
 * event-level, and putting either on a card would repeat one order-wide
 * artifact N times. The wallet badges sit in their own row directly under the
 * header, where they are the surface's headline next step rather than
 * order-metadata chrome. A non-"valid" ticket
 * (redeemed/cancelled) dims and desaturates its card and disables its
 * handover and wallet actions alike — the code is still literally scannable
 * at that opacity, but the door will reject it, and handing or wallet-adding
 * it forward would mislead whoever receives it; a label beside the control in
 * that same band names which of the two states it's in, since a dimmed card
 * alone doesn't say which. A GIVEN ticket does not dim: dimming is what redeemed
 * and cancelled mean, and a given ticket is valid — its own wallet and
 * handover actions stay live.
 *
 * Both wallets are offered on the same two surfaces, in the same two scopes,
 * so a buyer who can reach one can reach the other by the identical gesture.
 * The header row is the ORDER-level scope: every badge this order has, both
 * vendors, always — Google as the one bundled save link it mints per six
 * passes, Apple as the one signed `.pkpass` per ticket, since a `.pkpass` has
 * no bundle format. Each card's own `<ticket-wallet-actions>` is the
 * TICKET-level scope: an overlay carrying just that ticket's own passes and
 * that same card as an image file, for the buyer forwarding one ticket rather
 * than taking the lot. That control appears only on an order carrying more
 * than one ticket — at one ticket the order IS the ticket, so the header
 * already is the ticket-level surface and a second control would cost a tap
 * to answer a question the buyer cannot ask.
 *
 * Every ORDER-WIDE control is held inside one ceiling and withdraws at it
 * together — the two wallet marks and "save every ticket" alike
 * (order-access.state.ts's `pastBulkCeiling`) — because an order too big for
 * one vendor's single gesture is also an order whose N separate browser
 * downloads are a worse answer than the one the buyer already has: each card
 * carries its own control, which on an order that size is how the tickets
 * were always going to be handed out. One line stands where those controls
 * would have been, worded as information, since nothing failed and nothing is
 * missing.
 *
 * Under the rail stands ONE row, and it stands at every N. It holds — while
 * the book is MIXED, some tickets handed on and some not — a switch over
 * three named sets: the whole book, the part still the reader's own, the part
 * somebody else now holds, every number pressable, exactly one selected, and
 * the selected one the set the rail shows. The switch is a state condition and
 * never a count threshold: nothing to divide at zero handovers, nothing left
 * to say once every ticket has gone.
 * The row reserves its height unconditionally — 40px, the touch floor the
 * switch's chips stand on — so the switch arriving on a first handover, and
 * leaving when the last one is taken back, never moves the legal bar under it.
 * No chip names a number until the count has settled: "All 2" over three rows
 * promises a whole the rail beside it contradicts, and OrderAccess carries no
 * line-item total (order-access.state.ts), so this page's ticket count is
 * whatever has minted so far. Every numeral the row prints is written at this
 * order's digit width, so a count crossing ten changes what it says and not
 * how wide it is.
 *
 * Every branch closes on `<legal-footer>`, pinned to the foot of the scroll
 * box and standing in the content column itself — the mount carries the
 * column's own `mx-auto w-full max-w-2xl px-4`, so the plate resolves that one
 * padding box the way every other block on the page does, with nothing
 * measured off one element and written onto another: a buyer reaching this
 * page cold from a fulfillment email has no other route to imprint, terms and
 * privacy, and those links are statutory rather than conditional on the order
 * having resolved — so they stay on screen at every ticket count instead of
 * living below a fold a five-ticket order pushes them past.
 */
import { area, tooltip } from "@mhmo91/schmancy";
import {
	art,
	fromResizeObserver,
	fullHeight,
	overflowWithin,
} from "@mhmo91/schmancy/directives";
import type { SchmancyChipsChangeEvent } from "@mhmo91/schmancy/form";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { organizationPathPrefix } from "../../workspace/organization.state.ts";
// Side-effect: registers <schmancy-scroll> and <schmancy-scroll-rail>.
import "@mhmo91/schmancy/layout";
import { show } from "@mhmo91/schmancy/overlay";
// Side-effect: registers <schmancy-skeleton>.
import "@mhmo91/schmancy/skeleton";
import { Ticket } from "@momo/types";
import { css, html, type TemplateResult } from "lit";
import { state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	animationFrameScheduler,
	auditTime,
	catchError,
	concatMap,
	defer,
	distinctUntilChanged,
	EMPTY,
	exhaustMap,
	filter,
	finalize,
	from,
	fromEvent,
	map,
	merge,
	of,
	Subject,
	startWith,
	switchMap,
	takeUntil,
	tap,
	throwError,
	timer,
} from "rxjs";
import { track$ } from "../../analytics/analytics.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
// Side-effect: registers <legal-footer>.
import "../../legal/legal-footer.view.ts";
import { captureTicketCardImage } from "../../ticket-templates/capture.ts";
import {
	downloadTicketCardImage,
	ticketShareFileName,
} from "../../ticket-templates/share.ts";
import type {
	TicketCard,
	TicketCardEvent,
} from "../../ticket-templates/ticket-card.view.ts";
// Side-effect: registers <ticket-card>.
import "../../ticket-templates/ticket-card.view.ts";
import "../brand-bar.view.ts";
import "../event-header/event-header.view.ts";
// Side-effect: registers <google-wallet-badge>.
import "./google-wallet-badge.view.ts";
// Side-effect: registers <apple-wallet-badge>; the named import is what the
// wallet row's own vendor ordering reads.
import { isApplePlatform } from "./apple-wallet-badge.view.ts";
// Side-effect: registers <handover-sheet>.
import "./handover-sheet.view.ts";
import {
	carouselGivenAway,
	carouselStillYours,
	handoverByTicket,
	handoverTally,
} from "./handover.state.ts";
import {
	type ArtifactChip,
	artifactChips,
	failedTicketImageSave,
	type OrderAccessCarouselItem,
	type OrderAccessEventContext,
	type OrderAccessTicketRow,
	orderAccessCarousel,
	orderAccessEvent,
	orderAccessState,
	orderTicketCount,
	pastBulkCeiling,
	saveTicketImages$,
	savingTicketImage,
	walletBulkOffered,
} from "./order-access.state.ts";
// Side-effect: registers <ticket-wallet-actions>.
import "./ticket-wallet-actions.view.ts";

/** Champagne gold — order-card.view.ts's guest-order grammar, reused
 *  verbatim for the same job on a different surface: this ticket is no longer
 *  the holder's own. A concrete literal, not a theme token: `art` writes it
 *  into SVG fill/stroke ATTRIBUTES, where a `var(--…)` reference never
 *  resolves, and no theme token names this accent. The border class in
 *  `ticketCard` spells the literal again because Tailwind's scanner reads
 *  only literal strings in source. */
const HANDOVER_GOLD = "#d4af37";

/** The padded ring every card sits inside — the shape that turns gold once
 *  the ticket has been given, the host the `gift` overlay paints into, and the
 *  chrome layer the handover band composes on. One string, worn by a real card
 *  and by the placeholder standing in for one, so the two silhouettes cannot
 *  drift apart. */
const MAT = "rounded-3xl border p-2";

/** What that ring takes out of the card's own width: `border` is 1px and `p-2`
 *  is 8px, on each of two sides. Taken from the CARD rather than added to the
 *  rail item, so the carousel measures, snaps and paginates exactly as it did
 *  before every card gained a mat — which is also why this number and the
 *  string above have to move together. */
const MAT_INSET = 2 * (1 + 8);

/** The three sets the rail can be showing — the whole book, the part still
 *  the reader's own, the part somebody else now holds. Spelled as the copy
 *  keys they select verbatim, so a set is named once and the state and the
 *  catalog cannot drift into two words for it. */
type RailScope = "all" | "yours" | "given";

/** A count written at the widest count this page can show. A numeral that
 *  changes width drags every numeral after it sideways, and these change under
 *  the reader's own hand. Padded with U+2007 FIGURE SPACE, the one space
 *  exactly one digit wide that a screen reader still treats as whitespace. */
const steadyCount = (count: number, widest: number): string =>
	String(count).padStart(String(Math.max(widest, count)).length, "\u2007");

@schmancyElement("order-access")
export class OrderAccessView extends LocalizeMixin(SchmancyElement) {
	/** The wallet row's edge fades carry their gradient here rather than as
	 *  `bg-linear-to-l from-… to-transparent`, because Tailwind's gradient
	 *  utilities compile to `background-image: linear-gradient(var(--tw-
	 *  gradient-stops))` and this build registers no `--tw-gradient-*`
	 *  properties, so that variable resolves empty and every such pair
	 *  computes to `background-image: none` — measured, in the document and in
	 *  an adopted shadow sheet alike. Everything else about the fades (box,
	 *  inset, width, pointer-events) is a working utility on `class=`; only
	 *  the one declaration Tailwind cannot express lives here, on the surface
	 *  where presence is guaranteed.
	 *
	 *  The ground is the page's own `containerLowest`, named directly: a fade
	 *  that ends on any other tone paints a visible seam down the row instead
	 *  of dissolving into it. */
	static styles = [
		css`
			[data-wallet-fade="start"] {
				background-image: linear-gradient(
					to right,
					var(--schmancy-sys-color-surface-containerLowest),
					transparent
				);
			}
			[data-wallet-fade="end"] {
				background-image: linear-gradient(
					to left,
					var(--schmancy-sys-color-surface-containerLowest),
					transparent
				);
			}
		`,
	];

	private saveTwin = createRef<TicketCard>();
	@state() private sharingOrder = false;
	@state() private shareOutcome: "copied" | "failed" | null = null;
	private readonly shareRequested = new Subject<string>();

	/** Which of the three sets the rail is showing — the scope switch's own
	 *  value. A component field, not a singleton: it dies with the page rather
	 *  than scoping the next order's rail from a tap the buyer made on this
	 *  one. */
	@state() private railScope: RailScope = "all";

	/** Which edges of the wallet row still have marks past them — the row's
	 *  only overflow signal, since its scrollbar is hidden and a 5-pass order
	 *  runs ~1000px wide inside a 390px phone with nothing at the cut to say
	 *  so. Both false whenever the row fits, so a row with one Apple control
	 *  and one Google badge carries no fade at all: an affordance for a scroll
	 *  that cannot happen is chrome the surface has to explain away. */
	@state() private walletRowEdges: {
		readonly start: boolean;
		readonly end: boolean;
	} = { start: false, end: false };

	private walletRow = createRef<HTMLElement>();

	/** Fired whenever the wallet row may have moved or been re-rendered, and
	 *  joined into the one pipeline below rather than measured at each site.
	 *  Each wallet badge is its own element rendering in its own cycle, so while
	 *  this view's `updated()` runs the row still reports the width it had
	 *  before the badges landed — a measurement taken there reads "fits" on a
	 *  row about to be 1000px wide. */
	private rowsChanged = new Subject<void>();

	/** The card's on-screen width — the size `<ticket-card>` scales the native
	 *  384px design down to (`cardWidth` below). A pixel number,
	 *  not a `w-[86%]`/`sm:w-96` pair, because the card is laid out at a fixed
	 *  384 and transform-scaled whole: a fluid CSS width would instead re-flow
	 *  the composition, which is the one thing a ticket must never do. */
	@state() private viewportWidth = window.innerWidth;

	override connectedCallback() {
		super.connectedCallback();

		merge(
			merge(
				// The card is laid out at a fixed 384 and transform-scaled whole, so
				// its on-screen size is a function of the viewport and nothing else.
				fromEvent(window, "resize").pipe(
					tap(() => (this.viewportWidth = window.innerWidth)),
				),
				this.rowsChanged,
				// The wallet row's width is the page column minus whatever the page
				// scrollbar takes, and that scrollbar appears or vanishes whenever a
				// child settling its own content — a poster loading, a card's shadow
				// tree laying out — pushes total page height across the viewport: no
				// window resize fires and this view never re-renders, so its own
				// `updated()` cannot see it. The ref is empty until the first render,
				// which is why the element is picked up off a render rather than at
				// subscribe time.
				this.rowsChanged.pipe(
					map(() => this.walletRow.value),
					distinctUntilChanged(),
					switchMap((row) => (row ? fromResizeObserver(row) : EMPTY)),
				),
			).pipe(
				startWith(null),
				// One measurement per frame, taken after the frame's renders have
				// laid out — an audit rather than a debounce, so a flick keeps
				// answering every frame instead of waiting for the finger to stop.
				auditTime(0, animationFrameScheduler),
				tap(() => this.measureWalletRow()),
			),
			// One card at a time, each from a twin mounted for that card alone and
			// taken back down after it: the rail itself carries no capture node, so
			// a 6-ticket order draws six QR codes instead of twelve and the file a
			// buyer saves still comes off the same card rendering they are looking
			// at. A second tap while a run is going is dropped, never queued:
			// two runs share the one twin and would rasterize each other's card.
			saveTicketImages$.pipe(
				// Ids in, rows out: the carousel is where a ticket's place in the
				// order is settled, so the number printed on the saved file is the
				// number the card on screen shows.
				map(({ ticketIds, sourceTicketId }) => ({
					sourceTicketId,
					rows: orderAccessCarousel
						.get()
						.filter(
							(item): item is OrderAccessTicketRow =>
								item.kind === "ticket" &&
								ticketIds.includes(item.ticket.ticketId),
						),
				})),
				filter(({ rows }) => rows.length > 0),
				exhaustMap(({ rows, sourceTicketId }) =>
					from(
						rows.map((row, index) => ({
							row,
							sourceTicketId,
							done: index + 1,
							total: rows.length,
						})),
					).pipe(
						concatMap((job) =>
							of(job).pipe(
								tap(() => {
									savingTicketImage.replace(job);
								}),
								switchMap(() => from(this.updateComplete)),
								// The twin mounts inside that update, and the schmancy
								// elements composing the card flush on microtasks this
								// host's own updateComplete never awaits — so the capture
								// waits for the frame after them, where the twin has a
								// real 384px rect and painted content to rasterize.
								switchMap(() => timer(0, animationFrameScheduler)),
								switchMap(() => {
									const node = this.saveTwin.value?.captureNode();
									return node
										? from(captureTicketCardImage(node))
										: throwError(
												() =>
													new Error(
														"saveTicketImages: the capture twin never mounted",
													),
											);
								}),
								tap((blob) =>
									downloadTicketCardImage(
										blob,
										ticketShareFileName(
											orderAccessEvent.get()?.name ?? "",
											job.row.ticket.typeName,
										),
									),
								),
							),
						),
						tap({ complete: () => failedTicketImageSave.replace(null) }),
						// One failure ends the run and says so, rather than leaving the
						// buyer to work out which of N files never arrived.
						catchError((err) => {
							console.error(err);
							failedTicketImageSave.replace({ sourceTicketId });
							return EMPTY;
						}),
						// Runs on teardown too, so a page left mid-run leaves no card
						// standing in a wait nothing is answering.
						finalize(() => savingTicketImage.replace(null)),
					),
				),
			),
			this.shareRequested.pipe(
				exhaustMap((eventName) =>
					defer(() => {
						const url = window.location.href;
						return typeof navigator.share === "function"
							? from(
									navigator.share({
										url,
										text: this.t("storefront.orderAccess.shareOrder.text", {
											eventName,
										}),
									}),
								)
							: from(navigator.clipboard.writeText(url)).pipe(
									tap(() => {
										this.shareOutcome = "copied";
									}),
								);
					}).pipe(
						catchError((error: unknown) => {
							if (
								!(error instanceof DOMException && error.name === "AbortError")
							)
								this.shareOutcome = "failed";
							return EMPTY;
						}),
						finalize(() => {
							this.sharingOrder = false;
						}),
					),
				),
			),
		)
			.pipe(
				takeUntil(this.disconnecting),
				finalize(() => failedTicketImageSave.replace(null)),
			)
			.subscribe();
	}

	/** The edge fades live OUTSIDE the scroller, in a `relative` wrapper of
	 *  their own, because an absolutely-positioned child of a scroll container
	 *  is positioned against that container's padding box and then scrolls
	 *  with its content — measured: a fade pinned at the row's right edge sits
	 *  at x=358 unscrolled and x=-16 once the buyer has scrolled 374px, i.e.
	 *  off-screen exactly when it has something to signal. That is also why
	 *  `<schmancy-scroll fade>` is not what carries this.
	 *
	 *  Read from the live element rather than derived from the badge counts:
	 *  the marks are vendor artwork at brand-mandated sizes, in two locales,
	 *  and Apple's own render is platform-gated — a width predicted from any
	 *  of that is a second measurement free to disagree with the row's. */
	private measureWalletRow(): void {
		const row = this.walletRow.value;
		const scrollable = row ? row.scrollWidth - row.clientWidth : 0;
		// The 1px floor keeps a sub-pixel remainder from reading as a scroll and
		// lighting a fade the buyer can never travel to.
		const edges =
			row && scrollable > 1
				? { start: row.scrollLeft > 1, end: row.scrollLeft < scrollable - 1 }
				: { start: false, end: false };
		if (
			edges.start !== this.walletRowEdges.start ||
			edges.end !== this.walletRowEdges.end
		)
			this.walletRowEdges = edges;
	}

	override updated() {
		this.rowsChanged.next();
	}

	/** The rail: one horizontal row of ticket-shaped slots, present in every
	 *  state. A slot holds a real card once that ticket is minted and a
	 *  shimmering silhouette until then (order-access.state.ts's
	 *  `orderAccessCarousel` always yields at least one), so a ticket ARRIVES
	 *  IN PLACE — same width, same 2:3 box, same position — instead of
	 *  displacing whatever the page was showing while it waited. That is the
	 *  whole waiting state: no status card, no banner, no timed reassurance
	 *  copy, since a shimmering ticket already says "this is being made" and
	 *  they mint in seconds.
	 *  The scroller is the library's own (`<schmancy-scroll>`, the same
	 *  primitive the wallet row above already stands on) rather than a bare
	 *  overflow utility, and it hides its scrollbar, so it owes a signal at the
	 *  cut. `<schmancy-scroll-rail>` wraps it and states that signal where the
	 *  question is asked — a shade over whichever cut still has cards past it
	 *  and a chevron riding that fold, each absent the moment there is nothing
	 *  more that way and both absent while the whole rail fits, so an order
	 *  small enough to show whole carries none of it. That shade is this
	 *  surface's whole statement of position: the band says where it stands at
	 *  its own cut, so nothing beside it says it again. The mark is pressable
	 *  and steps one card, and it is out of the accessibility tree: every card
	 *  is already reachable in DOM order, so a reader and a keyboard gain
	 *  nothing from a shortcut over it.
	 *  `fade` stays off under it. The rail's edge unit is a card — a SURFACE
	 *  rather than ink, the case the scroller's own documentation excludes — so
	 *  a mask there has almost no tone to take and every figure printed on the
	 *  card to lose; the shade paints the ground instead, and a ramp stacked
	 *  under it would thin the same peeking card the shade is pointing at.
	 *  The cards ride an inner `w-max` track, which is what the mark's paging
	 *  reads its column stops off.
	 *  The rail leads at the column's own edge, so a card starts on the same
	 *  vertical as the masthead and the wallet badges above it however many
	 *  tickets the order holds — one ticket floating mid-column while every
	 *  other block hugs the leading edge is the break a buyer reads as a
	 *  misplacement. */
	private ticketRail(
		items: readonly OrderAccessCarouselItem[],
		event: OrderAccessEventContext | null,
		orderAccessToken: string | null,
	): TemplateResult {
		return html`
			<div class="flex flex-col gap-3">
				<schmancy-scroll-rail>
					<schmancy-scroll direction="horizontal" hide>
						<div class="flex w-max justify-start gap-4 pb-2">
							${repeat(
								items,
								(item, i) =>
									item.kind === "ticket"
										? item.ticket.ticketId
										: `pending-${i}`,
								(item) =>
									item.kind === "ticket" && event
										? this.ticketCard(item, event, orderAccessToken)
										: this.pendingTicketCard(),
							)}
						</div>
					</schmancy-scroll>
				</schmancy-scroll-rail>

				<!-- One row under the rail, holding its height at every N in every
				     state: the switch below arrives on a first handover and leaves when
				     the last one is taken back, and a row sized to what it happens to
				     hold would move the legal bar under it each time. Reserved is the
				     40px the chips stand on, which is the touch floor a control on this
				     surface has to clear.
				     The switch stands only while the book is MIXED (handover.state.ts's
				     handoverTally, which is also null while a ticket is still minting,
				     so no chip ever names a whole the rail beside it contradicts).
				     Required is what keeps a scope always in force: the container
				     ignores a press that would deselect the chosen chip. It drives each
				     child's selected state from its own value, which is why no chip
				     carries a selected attribute or a handler, and it emits change on a
				     press alone, never on the value bound here. No hint and no error, so
				     the container's hint region never renders — it would stand below the
				     chips, outside the height this row reserves. -->
				<div
					data-rail-row
					class="flex min-h-10 items-center"
				>
					${when(
						handoverTally.get(),
						(tally) => html`
							<schmancy-chips
								data-ticket-scope
								required
								class="w-full sm:w-auto"
								.value=${this.railScope}
								@change=${(e: SchmancyChipsChangeEvent) => {
									const next = e.detail.value;
									// Anything that is not one of the three named sets is the
									// whole book. Falling through instead would leave the rail
									// scoped while the container had just cleared every chip's
									// selected state — chips dark, rail still narrowed, the
									// exact ambiguity this switch exists to remove.
									this.railScope =
										next === "all" || next === "yours" || next === "given"
											? next
											: "all";
								}}
							>
								<schmancy-filter-chip size="sm" color="surface" variant="solid" value="all" class="tabular-nums">
									${this.t("storefront.orderAccess.handover.tally.all", {
										count: steadyCount(
											tally.given + tally.yours,
											orderTicketCount.get(),
										),
									})}
								</schmancy-filter-chip>
								<schmancy-filter-chip size="sm" color="surface" variant="solid" value="yours" class="tabular-nums">
									${this.t("storefront.orderAccess.handover.tally.yours", {
										count: steadyCount(tally.yours, orderTicketCount.get()),
									})}
								</schmancy-filter-chip>
								<schmancy-filter-chip size="sm" color="surface" variant="solid" value="given" class="tabular-nums">
									${this.t("storefront.orderAccess.handover.tally.given", {
										count: steadyCount(tally.given, orderTicketCount.get()),
									})}
								</schmancy-filter-chip>
							</schmancy-chips>
						`,
					)}
				</div>
			</div>
		`;
	}

	/**
	 * One page, one geometry, every state. The masthead, the rail, and the
	 * closing note render in the same order at the same places whether the
	 * order is still resolving, half-minted, whole, or void — what changes is
	 * what each slot HOLDS, never which slots exist. That is the whole point:
	 * a buyer watching their ticket appear should see it fill a shape that was
	 * already there, not watch the page rearrange itself around a status card
	 * that then disappears.
	 *
	 * The masthead leads the scroll region, standing in the page's own padding
	 * box: `<event-header>` is a card, so what that box holds to the column's
	 * edge is its PLATE — the edge a reader actually sees — on the same
	 * vertical as the ticket mat and the legal bar. The header's own
	 * `px-4 sm:px-6` is that card's interior and stays where it is, inset from
	 * the column exactly as the mat's own ring insets the ticket it holds.
	 * Its floating `actions` slot carries the order-wide
	 * .ics/receipt chips plus "Download tickets"/"Share order" in one row —
	 * every icon-button `size="sm"`, since `size="lg"` reads oversized on a
	 * narrow phone screen; the wallet badges keep their own brand-mandated
	 * 48dp floor regardless; the two rows don't need to share one height.
	 * The wallet badges (Google, Apple) sit as their own row
	 * directly under `<event-header>` — a sibling of the header rather than
	 * content projected into it, since the wallet actions are the surface's
	 * own headline next step, not order-metadata chrome the header's actions
	 * slot carries — horizontally-scrolling (`<schmancy-scroll
	 * direction="horizontal">`), ordered by the buyer's own platform — Apple
	 * first on an Apple device, Google first everywhere else. Ordering is the
	 * only thing the platform decides: both badges render whenever they have a
	 * pass to offer, so neither vendor is reachable by a gesture the other
	 * isn't. Each badge contributes its marks as this row's own children rather
	 * than a wrapper (apple-wallet-badge.view.ts), so a row wider than the
	 * screen overflows and scrolls at full brand size instead of folding into
	 * stacked lines. That row hides its scrollbar, so an edge fade painted on
	 * the page's own ground is what says there is more past the cut —
	 * `measureWalletRow` below, and only where the row actually overflows. The
	 * row takes that column's inline padding from the column itself rather than
	 * declaring its own, so each 32px fade lies over marks; declared on the
	 * scroller, half of every fade would paint bare padding instead.
	 * The whole row is held inside one ceiling — the lower of the two vendors'
	 * own maxima for a single add (order-access.state.ts's
	 * WALLET_BULK_PASS_CEILING). Above it neither vendor's order-level mark
	 * renders, the row is not painted at all, and in its place one line names
	 * the per-ticket route — which on an order that size is how the passes were
	 * always going to be handed out, one guest at a time. Nothing failed and
	 * nothing is missing, so it is worded as information; it renders only for a
	 * session some vendor could actually have served. All
	 * of it renders once `event` resolves, since a link to share or a ticket
	 * to download don't exist before then.
	 */
	override render() {
		const data = orderAccessState.value;

		// The one frame that is not this page: the link resolved to no order at
		// all, so there is no event to name and no ticket to hold a place for.
		if (data.status === "not-found")
			return html`
				<schmancy-surface ${fullHeight()} type="containerLowest" opacity="solid" class="relative w-full select-none touch-manipulation">
					<div ${overflowWithin({ direction: "vertical" })} class="flex h-full flex-col">
						<!-- Pinned and glass, the mirror of the bar at the other end of
						     this same box: sticky binds to the nearest scrolling ancestor,
						     which is this scroll box, so what little the frame carries
						     passes under the strip blurred instead of ending at it. -->
						<schmancy-surface
							type="containerLowest"
							elevated
							class="sticky top-0 z-10 mx-auto block w-full max-w-2xl"
						>
							<brand-bar class="block px-4 py-3"></brand-bar>
						</schmancy-surface>
						${this.notFound()}
						<!-- The auto margin floors the bar on a frame that never fills
						     the box; the sticky offset takes over the moment it does.
						     No ground of its own: legal-footer's own plate is this page's
						     ground tone, opaque, so what passes under it is hidden. The
						     column's own four classes stand on the mount, so the plate
						     resolves the same padding box notFound() declares below and
						     the logo starts on the same vertical as the copy above it. -->
						<legal-footer
							class="sticky bottom-0 z-10 mx-auto mt-auto w-full max-w-2xl px-4"
						></legal-footer>
					</div>
				</schmancy-surface>
			`;

		const event = orderAccessEvent.get();
		const saving = savingTicketImage.value;
		const headerSaving = saving?.sourceTicketId === null ? saving : null;
		const orderAccessToken =
			"orderAccessToken" in data ? data.orderAccessToken : null;
		const carousel = orderAccessCarousel.get();
		// Above the ceiling both vendors' order-level marks are withheld
		// together (order-access.state.ts's WALLET_BULK_PASS_CEILING), so the
		// row itself has nothing left to hold and the line below takes up the
		// answer in its place. The line answers for controls, so it reaches
		// exactly the sessions that would have been offered one: a session no
		// vendor can serve at all — no Google issuer, and a device whose OS
		// cannot open a `.pkpass` — never asked the question it answers.
		const bulkOffered = walletBulkOffered.get();
		const aboveCeiling = pastBulkCeiling.get();
		// Order-wide by construction, never the scoped rail's subset: "download
		// all tickets" means all of them, and reaching them through the rows
		// rather than through mounted cards is what lets a card the rail is not
		// currently showing still be saved.
		const validRows = carousel.filter(
			(item): item is OrderAccessTicketRow =>
				item.kind === "ticket" && item.ticket.status === "valid",
		);
		// A voided order says so where the page's closing note sits — one line,
		// no card: the tickets above already read as void (dimmed, captioned). A
		// live order has nothing left to say here — the tickets above already
		// are the confirmation.
		const closing =
			data.status === "refunded" || data.status === "cancelled"
				? this.t(`storefront.orderAccess.voided.${data.status}Body`)
				: null;

		return html`
			<schmancy-surface ${fullHeight()} type="containerLowest" opacity="solid" class="relative w-full select-none touch-manipulation">
				<!-- ONE scroll box, and the masthead is inside it: everything above the
				     rail is content, not chrome — the poster, the wallet marks, the
				     order-wide actions — and holding it in a row of its own outside the
				     scroller costs half the viewport permanently to a header a buyer
				     reads once, leaving a 471px ticket in a 354px window on a laptop.
				     Scrolled with the rail it moves out of the way and the card has the
				     whole screen again. What stays put is the footer alone, which is a
				     property of where it sits in this box, not of a row: sticky pins
				     against the nearest scrolling ancestor whatever else scrolls inside
				     it.
				     One axis, and it says so: every wide thing here — the wallet row,
				     the ticket rail — carries its own horizontal scroller, so this box
				     has nothing of its own to scroll sideways. The default both leaves
				     it accepting one, and a sliver is all it takes: the rail's trailing
				     chevron hangs a third of its 44px out over the cut and reaches 3px
				     further on its idle animation, which clears the column's 16px
				     gutter by 1.67px — measured as 2px of range the whole page then
				     drags sideways on under a thumb, on a phone alone, since a wider
				     screen caps the column and the gutter grows past the reach. -->
				<div ${overflowWithin({ direction: "vertical" })} class="flex h-full flex-col">
					<div class="mx-auto w-full max-w-2xl px-4">
						<event-header>
							${when(
								event,
								(event) => html`
									<div slot="actions" class="flex flex-wrap items-center gap-2">
										<div data-artifact-rail class="contents">
											${repeat(
												artifactChips.get(),
												(chip) => chip.kind,
												(chip) => this.artifactChip(chip),
											)}
										</div>
										<!-- Withdrawn above the ceiling with the wallet row, for the
										     same reason and in the same line below: an order that size
										     is handed out one ticket at a time, and each card's own
										     control is where it is handed out from. While a run is
										     going the glyph gives its square over to the run's own
										     position — the box is fixed by size, so the readout
										     lands in the rectangle the icon just left. -->
										${when(
											validRows.length > 0 && !aboveCeiling,
											() => html`
												<schmancy-icon-button
													data-save-tickets
													icon=${failedTicketImageSave.value?.sourceTicketId === null ? "refresh" : "download"}
													?text=${headerSaving !== null}
													size="sm"
													variant="filled tonal"
													color=${failedTicketImageSave.value?.sourceTicketId === null ? "error" : "neutral"}
													?disabled=${headerSaving !== null}
													.busy=${headerSaving !== null}
													aria-label=${this.downloadTicketsLabel()}
													${tooltip(this.downloadTicketsLabel())}
													@click=${() => {
														if (savingTicketImage.value !== null) return;
														track$.next({
															name: "order_action",
															action: "download",
														});
														saveTicketImages$.next({
															ticketIds: validRows.map(
																(row) => row.ticket.ticketId,
															),
															sourceTicketId: null,
														});
													}}
												>${when(
													headerSaving,
													(saving) => html`
														<schmancy-typography type="label" token="sm" class="tabular-nums">
															${this.t(
																"storefront.orderAccess.downloadTickets.progress",
																{
																	done: saving.done,
																	total: saving.total,
																},
															)}
														</schmancy-typography>
													`,
												)}</schmancy-icon-button>
											`,
										)}
										<schmancy-icon-button
											data-share-order
											icon=${this.shareOutcome === "failed" ? "refresh" : this.shareOutcome === "copied" ? "check" : "ios_share"}
											size="sm"
											variant="filled tonal"
											color=${this.shareOutcome === "failed" ? "error" : "neutral"}
											aria-label=${this.t(this.shareOutcome === "failed" ? "storefront.orderAccess.shareOrder.error" : this.shareOutcome === "copied" ? "storefront.orderAccess.shareOrder.savedToast" : "storefront.orderAccess.shareOrder.ariaLabel")}
											.busy=${this.sharingOrder}
											${tooltip(this.t(this.shareOutcome === "failed" ? "storefront.orderAccess.shareOrder.error" : this.shareOutcome === "copied" ? "storefront.orderAccess.shareOrder.savedToast" : "storefront.orderAccess.shareOrder.ariaLabel"))}
											@click=${() => this.shareOrder(event.name)}
										></schmancy-icon-button>
									</div>
								`,
							)}
						</event-header>
						${when(
							event,
							() => html`
								${when(
									bulkOffered,
									() => html`
										<div class="relative mt-3">
											<schmancy-scroll
												${ref(this.walletRow)}
												direction="horizontal"
												hide
												class="flex items-center gap-2 py-1"
												@scroll=${() => this.rowsChanged.next()}
											>
												${when(
													isApplePlatform(),
													() => html`
														<apple-wallet-badge></apple-wallet-badge>
														<google-wallet-badge></google-wallet-badge>
													`,
													() => html`
														<google-wallet-badge></google-wallet-badge>
														<apple-wallet-badge></apple-wallet-badge>
													`,
												)}
											</schmancy-scroll>
											${when(
												this.walletRowEdges.start,
												() => html`
													<div
														data-wallet-fade="start"
														aria-hidden="true"
														class="pointer-events-none absolute inset-y-0 left-0 w-8"
													></div>
												`,
											)}
											${when(
												this.walletRowEdges.end,
												() => html`
													<div
														data-wallet-fade="end"
														aria-hidden="true"
														class="pointer-events-none absolute inset-y-0 right-0 w-8"
													></div>
												`,
											)}
										</div>
									`,
								)}
							`,
						)}
					</div>
					<div
						data-content-rail
						class="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 pt-6 pb-6"
					>
						${this.ticketRail(
							// The scope releases with the switch that set it: a book that
							// stops being mixed — the last ticket handed on, every one taken
							// back — takes the switch away, and a rail still scoped by a
							// control nobody can reach any more would be showing a subset
							// with nothing left on the page to say so. A book that turns
							// mixed again reopens on the reader's last choice, which is
							// memory rather than staleness, because the switch is back on
							// screen with that choice visibly selected.
							handoverTally.get() === null || this.railScope === "all"
								? carousel
								: this.railScope === "yours"
									? carouselStillYours.get()
									: carouselGivenAway.get(),
							event,
							orderAccessToken,
						)}
						${when(
							closing,
							() => html`
								<schmancy-typography type="body" token="sm" class="px-1 text-surface-onVariant">
									${closing}
								</schmancy-typography>
							`,
						)}
					</div>
					<!-- The auto top margin is what makes this the FOOT of the scroll
					     box rather than a block trailing the closing note: a loading or
					     single-ticket order leaves the box with room to spare, and a
					     footer sitting mid-air under the last element reads as content
					     that ran out. Once the page is taller than the box that margin
					     has nothing left to take and the sticky offset carries the
					     footer instead, on the page's own ground tone rather than glass,
					     so what passes under the bar is hidden and the column's own pb-6
					     is the gap the last ticket comes to rest in. It carries the
					     content column's own four classes rather than this box's width,
					     so the plate resolves the identical padding box and the logo and
					     the links start on the same vertical as every line above them —
					     one box the page declares, read the same way by every block
					     standing in it. -->
					<legal-footer
						class="sticky bottom-0 z-10 mx-auto mt-auto w-full max-w-2xl px-4"
					></legal-footer>
				</div>
				<!-- The rasterize target, mounted for exactly as long as one card is
				     being saved and for no card otherwise: every rail card that carried
				     capture was a second, off-screen 384px render of the same ticket,
				     so an order's whole rail drew twice the QR codes it shows for a
				     file most buyers never ask for. Parked off-viewport rather than
				     hidden because the rasterizer clones computed style faithfully and
				     paints nothing for a hidden node, and sized by the card's own
				     default 384 because the capture node's rect must be that number
				     unconditionally. -->
				${when(saving, (saving) =>
					when(
						event,
						(event) => html`
							<div
								aria-hidden="true"
								class="pointer-events-none absolute top-0 -left-[9999px]"
							>
								<ticket-card
									${ref(this.saveTwin)}
									.ticket=${saving.row.ticket}
									.event=${this.cardEvent(event)}
									.sequence=${saving.row.sequence}
									capture
								></ticket-card>
							</div>
						`,
					),
				)}
			</schmancy-surface>
		`;
	}

	/** The chip's own name, spoken and on hover alike — one string, so the two
	 *  can't say different things about the same run. */
	private downloadTicketsLabel(): string {
		const saving =
			savingTicketImage.value?.sourceTicketId === null
				? savingTicketImage.value
				: null;
		if (failedTicketImageSave.value?.sourceTicketId === null)
			return this.t("storefront.orderAccess.downloadTickets.error");
		return saving === null
			? this.t("storefront.orderAccess.downloadTickets.ariaLabel")
			: this.t("storefront.orderAccess.downloadTickets.preparingAriaLabel", {
					done: saving.done,
					total: saving.total,
				});
	}

	/** A card's on-screen width: 72% of the viewport, so the next ticket peeks
	 *  in at the carousel's edge and says "there are more", capped at the mat
	 *  plus the card's own native 384 — the width at which `<ticket-card>`
	 *  receives exactly its design width and renders at scale 1.0, so a screen
	 *  with room for the whole ticket gets it whole rather than shrunk on a
	 *  viewport that never asked. The peek survives that cap because the RAIL
	 *  stops widening first, at the column's own `max-w-2xl`: one whole card
	 *  and 222px of the next is the steady frame at every width from 672px up,
	 *  and under the cap the 72% carries the peek alone — 42px at 320, 57px at
	 *  375, 86px at 480. The QR scans at every one of those, the card being
	 *  one whole SVG-backed composition scaled as a unit rather than a
	 *  rasterized shrink. */
	private get cardWidth(): number {
		return Math.round(Math.min(MAT_INSET + 384, this.viewportWidth * 0.72));
	}

	/** One ticket — the REAL card (`<ticket-card>`), the same Lit+schmancy
	 *  rendering the ticket design is authored as, never a stand-in, laid out
	 *  at its native 384px and scaled whole so the composition is identical on
	 *  every screen. The card is told only what this page holds differently
	 *  from an ops surface — the ticket, the event/venue snapshot, the
	 *  position in the order; it derives its own display id and brand, so the
	 *  reference here is the reference on the operator's screen. It carries no
	 *  `capture` twin: the one rasterize target this page mounts is the
	 *  on-demand twin in `render()`, so a rail card is one render of one ticket.
	 *
	 *  The card sits inside a padded MAT that every card carries, gold-bordered
	 *  once the ticket has been given and transparent-bordered until then, so
	 *  giving a ticket recolors a shape that was already in the rail rather
	 *  than reflowing it. The mat, not the card, hosts the `gift` overlay:
	 *  `art` paints at `z-index: -1` behind its host's own content, which on an
	 *  opaque `<ticket-card>` is nothing at all, while on the mat the warmth and
	 *  its raking highlight land in the visible ring around the card. `MAT_INSET`
	 *  comes out of the card's own width rather than the rail item's, so the
	 *  carousel's geometry is the same with the mat as without it.
	 *
	 *  A band straddles the card's bottom-left corner, composed on the mat
	 *  rather than over the card's own text, so each control and the ticket it
	 *  speaks for are one thing to read and on a given ticket the band sits
	 *  inside the gold frame that explains it. The handover button anchors the
	 *  band's left edge and IS the question while the ticket is still this
	 *  page's own ("Für wen?") and the answer once it is not ("Für Lena ·
	 *  persönlich") — tapping the answer reopens the sheet, so there is no
	 *  separate caption to hold in step with it and no "change" verb to learn.
	 *  It is text, not an icon — "give this ticket to someone" is not a glyph
	 *  anyone guesses, and it is the one action here that changes who holds a
	 *  credential — and its accessible name carries the ticket's own type and
	 *  display id, since the visible words alone would give every card's
	 *  control the identical name and a reader would hear "Für wen?" five
	 *  times with nothing to tell the tickets apart. The answer wearing the
	 *  holder's name is also the accessible correlate the gold alone cannot
	 *  be. Beside it, and only on a ticket this page can no longer give,
	 *  stands the redeemed/cancelled label — the ticket's own state at the
	 *  door, which speaks for the ticket rather than for the control.
	 *
	 *  `<ticket-wallet-actions>` mirrors the handover button across the same
	 *  band, anchored to its right edge. Its own visibility rule
	 *  (walletMultiTicket) is unchanged, so it mounts unconditionally here and
	 *  stays silent on a single-ticket order. `orderAccessToken` is null on
	 *  every branch that reaches this method with no real token yet (a
	 *  still-partial fulfillment, a refunded or cancelled order) — exactly the
	 *  branches where that same visibility rule already keeps the control from
	 *  rendering, so it never needs one. It asks for its ticket's image on the
	 *  same signal the header chip asks on, and reads the wait off the same
	 *  one — this view owns the twin every save is rasterized from, and needs
	 *  to tell it nothing. */
	private ticketCard(
		row: OrderAccessTicketRow,
		event: OrderAccessEventContext,
		orderAccessToken: string | null,
	): TemplateResult {
		const ticket = row.ticket;
		const held = handoverByTicket.get().get(ticket.ticketId) ?? null;
		// A card this page can no longer act on still says who holds it. Its
		// ticket travelled on from an ancestor's link rather than from this one,
		// so the page shows the name and offers nothing: both giving it again
		// and taking it back are the other holder's to do, and the server
		// refuses either from here.
		const canHandOver =
			ticket.status === "valid" && (held === null || held.reclaimable);
		const displayId = Ticket.ticketDisplayId(ticket.ticketId);
		// The control's own answer once the ticket has been given, and the line
		// its accessible name then ends on — composed once, so the seen and the
		// spoken cannot say different things.
		const holderLine =
			held === null
				? null
				: this.t("storefront.orderAccess.handover.heldBy", {
						name: held.name,
						channel: this.t(
							`storefront.orderAccess.handover.channel.${held.sentVia ?? "inPerson"}`,
						),
					});
		return html`
			<div
				data-ticket-card=${ticket.ticketId}
				${when(held, () => art({ name: "gift", color: HANDOVER_GOLD }))}
				class=${this.classMap({
					[MAT]: true,
					"flex-none": true,
					// #d4af37 — champagne gold (module doc above).
					"border-[#d4af37]/40": held !== null,
					"border-transparent": held === null,
				})}
				style="width:${this.cardWidth}px"
			>
				<div
					role="group"
					aria-label=${this.t("storefront.orderAccess.ticketAriaLabel", { displayId, typeName: ticket.typeName })}
					class=${this.classMap({ "opacity-40 grayscale": !canHandOver })}
				>
					<ticket-card
						.ticket=${row.ticket}
						.event=${this.cardEvent(event)}
						.sequence=${row.sequence}
						.width=${this.cardWidth - MAT_INSET}
					></ticket-card>
				</div>
				<!-- ONE band for the TWO controls it hosts — the handover button
				     on the left, the wallet trigger mirrored to the right edge —
				     straddling the card's bottom-left corner. The -mt-[6.25%] is
				     the 24px of empty padding both card variants reserve below
				     their last text line (ticket-inapp.editorial.template.ts's
				     columns, padding-block 1.5rem; ticket-inapp.strata.template.ts's
				     bottom band, py-6) as a fraction of the card's own 384px design
				     width: a percentage margin resolves against the containing
				     block's WIDTH — the mat's content box, which is exactly the
				     width the card was scaled to — so the overlap tracks that scale
				     and the band's own top edge never reaches the display id, the
				     brand line or the scan caption. A fixed 16px holds only above a
				     381px viewport and cuts into the brand line on every phone
				     narrower than that. The rest of the band rides the mat's own
				     ring, so on a given ticket both controls read inside the gold
				     frame that explains it. -->
				<div class="-mt-[6.25%] flex min-h-9 items-end justify-between gap-2 px-1">
					<div class="flex min-w-0 items-end gap-2">
						<schmancy-button
							variant="filled tonal"
							size="sm"
							color="neutral"
							class="min-w-0 max-w-full shadow-md"
							?disabled=${!canHandOver}
							aria-label=${
								holderLine === null
									? this.t("storefront.orderAccess.handover.whoForAriaLabel", {
											typeName: ticket.typeName,
											displayId,
										})
									: this.t("storefront.orderAccess.handover.heldByAriaLabel", {
											typeName: ticket.typeName,
											displayId,
											holder: holderLine,
										})
							}
							@click=${() =>
								show(
									() => html`
										<handover-sheet
											.seedTicketId=${held ? null : ticket.ticketId}
											.editDraftId=${held?.draftId ?? null}
										></handover-sheet>
									`,
								)
									.pipe(takeUntil(this.disconnecting))
									.subscribe()}
						>
							<!-- A holder name may run to the schema's 78 characters, and
							     the button host neither shrinks nor wraps, so the answer
							     ellipsises inside the width the band allows it. -->
							<span class="block min-w-0 truncate">
								${holderLine ?? this.t("storefront.orderAccess.handover.whoFor")}
							</span>
						</schmancy-button>
						${when(
							ticket.status !== "valid",
							() => html`
								<schmancy-typography
									type="label"
									token="sm"
									transform="uppercase"
									class="min-w-0 truncate tracking-widest text-surface-onVariant"
								>
									${this.t(`storefront.orderAccess.ticketStatus.${ticket.status}`)}
								</schmancy-typography>
							`,
						)}
					</div>
					${when(
						orderAccessToken,
						(token) => html`
							<ticket-wallet-actions
								.ticket=${row.ticket}
								.orderAccessToken=${token}
							></ticket-wallet-actions>
						`,
					)}
				</div>
			</div>
		`;
	}

	/** The one thing this page holds differently from an ops surface: a venue
	 *  snapshot OrderAccess froze at fulfillment, every field nullable. An
	 *  absent name/address collapses to "" rather than reaching for the
	 *  `Location` doc a tokenless reader can't see. Composed here and nowhere
	 *  else, because the rail's card and the off-screen twin a save rasterizes
	 *  are the same card and a venue resolved twice is a venue free to print
	 *  two different addresses. */
	private cardEvent(event: OrderAccessEventContext): TicketCardEvent {
		return {
			name: event.name,
			logoUrl: event.logoUrl,
			start: event.schedule.start.toDate(),
			end: event.schedule.end.toDate(),
			timezone: event.timezone,
			venue: {
				name: event.venue ?? "",
				address: event.address ?? "",
				notes: event.notes,
			},
		};
	}

	/** The carousel's trailing row during "partial" fulfillment — the same 2:3
	 *  silhouette a real card resolves to, inside the same mat every real card
	 *  wears, so the eventual real ticket lands in the exact box this one
	 *  occupies rather than 18px off it. */
	private pendingTicketCard(): TemplateResult {
		return html`
			<div
				data-ticket-card="pending"
				class=${this.classMap({
					[MAT]: true,
					"flex-none": true,
					"border-transparent": true,
				})}
				style="width:${this.cardWidth}px"
				aria-hidden="true"
			>
				<schmancy-skeleton class="aspect-[2/3] w-full rounded-3xl"></schmancy-skeleton>
			</div>
		`;
	}

	/** One .ics/receipt/wallet action, floated in `<event-header>`'s `actions`
	 *  slot alongside "Download tickets"/"Share order" — an icon-only
	 *  `schmancy-icon-button` (`filled tonal`, `color="neutral"`), the same
	 *  treatment this file already gives every other floating action (the
	 *  per-card share button): a labeled pill row here
	 *  would just restate in words what the icon already says. `artifactChips`
	 *  already excludes a `never` slot entirely, so every action reaching here
	 *  is either `pending` (dimmed, its icon already swapped by state.ts, no
	 *  href) or ready (its own live href) — never a dead, clickable link.
	 *  `schmancy-icon-button` carries no `target` property, so the one chip
	 *  that must open in a new tab (the receipt) skips `href` and opens via
	 *  `window.open` on click instead; every other kind navigates through
	 *  `href` directly, which the component itself already suppresses while
	 *  `disabled`. The accessible name is `aria-label` — a pending action's
	 *  own wording names the wait explicitly, not just its dimmed opacity —
	 *  and `aria-busy` says the same to a reader who never sees the opacity.
	 *  `size="sm"` fixes the control's box, so a pending chip and the live one
	 *  it becomes occupy the identical rectangle and only the glyph changes. */
	private artifactChip(chip: ArtifactChip): TemplateResult {
		const label = this.t(`storefront.orderAccess.artifacts.${chip.kind}`);
		const ariaLabel = chip.pending
			? this.t("storefront.orderAccess.artifacts.preparing", {
					artifact: label,
				})
			: label;
		return html`
			<schmancy-icon-button
				icon=${chip.icon}
				size="sm"
				variant="filled tonal"
				color="neutral"
				href=${ifDefined(!chip.opensNewTab ? (chip.href ?? undefined) : undefined)}
				?disabled=${chip.pending}
				.busy=${chip.pending}
				aria-label=${ariaLabel}
				${tooltip(ariaLabel)}
				@click=${() => {
					if (chip.pending) return;
					// The calendar entry is a retrieval channel; the receipt is not
					// one of the five the timeline records. Reported before the href
					// navigates, since that navigation is what leaves the page.
					if (chip.kind === "ics")
						track$.next({ name: "order_action", action: "ics" });
					if (!chip.href || !chip.opensNewTab) return;
					window.open(chip.href, "_blank", "noopener");
				}}
			></schmancy-icon-button>
		`;
	}

	/** Shares this order's own URL — never a per-ticket image — trying the
	 *  native share sheet first and falling back to the clipboard where
	 *  unsupported. `window.location.href` is already the canonical
	 *  `/order#t=<token>` link by the time this view mounts real data: the
	 *  mounted app's own order-access.sync heals the browser URL to that
	 *  shape first, so there is no token to reconstruct here. Mirrors
	 *  order-detail.view.ts's own copy/share-link handling — an aborted
	 *  share sheet is the buyer changing their mind, not a failure. */
	private shareOrder(eventName: string): void {
		if (this.sharingOrder) return;
		this.sharingOrder = true;
		this.shareOutcome = null;
		this.shareRequested.next(eventName);
	}

	/** This branch's own content column, and therefore what the footer measures
	 *  against: the page's reading measure and inline padding sit here rather
	 *  than on a wrapper around it, so the empty state, the brand bar above it
	 *  and the footer below all start on one vertical. */
	private notFound(): TemplateResult {
		return html`
			<div
				data-content-rail
				class="mx-auto flex w-full max-w-2xl flex-col items-center gap-3 px-4 py-16 text-center"
			>
				<schmancy-icon class="text-4xl text-surface-onVariant">link_off</schmancy-icon>
				<schmancy-typography type="headline" token="sm">${this.t("storefront.orderAccess.notFound.title")}</schmancy-typography>
				<schmancy-typography type="body" token="md" class="max-w-sm text-surface-onVariant">
					${this.t("storefront.orderAccess.notFound.body")}
				</schmancy-typography>
				<schmancy-button
					variant="filled"
					color="neutral"
					@click=${() =>
						area.push({
							area: "root",
							component: "storefront",
							path: organizationPathPrefix.get() || "/",
							historyStrategy: "push",
						})}
				>
					${this.t("storefront.orderAccess.notFound.cta")}
				</schmancy-button>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"order-access": OrderAccessView;
	}
}
